/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import createMethodDecorator from 'apmui/util/createMethodDecorator';

/**
 * The @debounce decorator delays invoking of the method until after
 * 'wait' ms have elapsed since the last time debounced method was invoked.
 *
 * The decorated method will invoke original method with the last arguments provided
 * and return the result of the last invocation (initial calls return null).
 *
 * @param leading  invoke function immediately as well as after timeout.
 * @example
 * ```
 *
 * class SomeClass {
 *      constructor() {
 *          document.addEventListener('keyup', this.onKeyUp.bind(this));
 *      }
 *
 *      @debounce(300)
 *      onKeyUp() {
 *         // ...
 *      }
 * }
 * ```
 */
export default function debounce(wait: number, leading = false) {
    return createMethodDecorator('debounce', (target, fn) => {
        let timer: number | null = null;
        let lastResult: any = null;
        let lastArguments: any = null;
        // use non arrow function for correct 'this' and to avoid copying arguments
        /* eslint-disable prefer-rest-params */
        return function(this: any) {
            lastArguments = arguments;
            const nextTimer = window.setTimeout(() => {
                lastResult = fn.apply(this, lastArguments);
                timer = null;
            }, wait);

            if (timer) {
                window.clearTimeout(timer);
            } else if (leading) {
                lastResult = fn.apply(this, lastArguments);
            }

            timer = nextTimer;
            return lastResult;
        };
    });
}
